import { yupResolver } from '@hookform/resolvers/yup'
import clsx from 'clsx'
import dayjs from 'dayjs'
import React, { Activity, useEffect, useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ControlledSystemDialog } from '@cms/components/data/Dialog'
import { SystemSwitch } from '@cms/components/data/Switch'
import { useSnackbar } from '@cms/providers/SnackbarProvider'
import { POST_SET_VALUE_OPTIONS } from '@cms/utils/constants'
import { ISystemStory, TWorkflowAction, WORKFLOW_ACTIONS, WORKFLOWS } from '@dl/declarations'
import { StoryFormData, StoryWorkflowFormData, workflowSchema } from '../../../utils/forms/story'
import { generateFormErrorsTooltip, generateSearchParam, renderDateTime } from '../../../utils/helpers'
import { useAppDispatch, useAppSelector } from '../../../utils/store/hooks'
import { cancelStoryWorkflowAsyncAction, updateStoryWorkflowAsyncAction } from '../../../utils/store/slices/editor.slice'
import { Button, ButtonGroup } from '../../data/Button'

import 'react-datepicker/dist/react-datepicker.css'

interface ICommonStoryModalContentProps {
    type: TWorkflowAction;
}

interface ICommonStoryMenuProps extends ICommonStoryModalContentProps {
    buttonState: {
        type: string;
        tooltip: string;
    }
    buttonProps?: React.ComponentProps<typeof Button>;
}

const StoryWorkflowModalContent: React.FC<ICommonStoryModalContentProps> = (props) => {
    const { type: workflowType } = props

    const { story } = useAppSelector((state) => state.editor)
    const { watch, setValue } = useFormContext<StoryWorkflowFormData>()

    const [matchDisplayTimeToPublishTime, setMatchDisplayTimeToPublishTime] = useState(true)
    const [scheduleType, setScheduleType] = useState<'immediate' | 'scheduled'>('immediate')
    const [
        publish_date_value,
        display_date_value
    ] = watch([
        'publish_date_value',
        'display_date_value'
    ])

    const handleDateChange = (field: 'publish_date_value' | 'display_date_value', date: Date | null) => {
        if (!date) {
            setValue(field, 'immediate', POST_SET_VALUE_OPTIONS)
            return
        }

        const utcDate = date.toISOString()
        setValue(field, utcDate, POST_SET_VALUE_OPTIONS)
    }

    const handlePublishDateChange = (date: Date | null) => {
        handleDateChange('publish_date_value', date)
        if (matchDisplayTimeToPublishTime) {
            handleDateChange('display_date_value', date)
        }
    }

    const handleDisplayDateChange = (date: Date | null) => {
        handleDateChange('display_date_value', date)
    }

    const handleToggleMatchDisplayTimeToPublishTime = (checked: boolean) => {
        setMatchDisplayTimeToPublishTime(checked)

        if (checked) {
            setValue('display_date_value', publish_date_value, POST_SET_VALUE_OPTIONS)
        }
        else {
            if (story?.story_display_date) {
                setValue('display_date_value', new Date(story?.story_display_date).toISOString())
            }
            else {
                setValue('display_date_value', publish_date_value, POST_SET_VALUE_OPTIONS)
            }
        }
    }

    useEffect(() => {
        if (workflowType === WORKFLOW_ACTIONS.Unpublish) {
            setValue('publish_date_value', 'immediate', POST_SET_VALUE_OPTIONS)
            setValue('display_date_value', 'immediate', POST_SET_VALUE_OPTIONS)
        }
    }, [workflowType])

    useEffect(() => {
        if (scheduleType === 'immediate') {
            setValue('publish_date_value', 'immediate', POST_SET_VALUE_OPTIONS)

            if (matchDisplayTimeToPublishTime) {
                setValue('display_date_value', 'immediate', POST_SET_VALUE_OPTIONS)
            }
        }
    }, [scheduleType])

    useEffect(() => {
        if (story?.story_first_publish_date) {
            setMatchDisplayTimeToPublishTime(false)
        }

        if (story?.story_display_date) {
            setValue('display_date_value', new Date(story?.story_display_date).toISOString())
        }

        if (["Publish", "Update"].includes(workflowType) && story?.scheduleObject?.action_data) {
            setScheduleType('scheduled')
            setValue('publish_date_value', new Date(story?.scheduleObject?.action_data?.publish_date).toISOString())
            setValue('display_date_value', new Date(story?.scheduleObject?.action_data?.display_date).toISOString())
        }
    }, [workflowType, story])

    const displayed_display_date_value = display_date_value !== 'immediate' ? new Date(display_date_value) : new Date()

    return (
        <React.Fragment>
            <p className='mb-2'>When do you want to {workflowType}?</p>

            <div className='flex flex-col gap-2 mb-4'>
                <label className='flex items-center gap-2'>
                    <input
                        type="radio"
                        value="immediate"
                        checked={scheduleType === 'immediate'}
                        onChange={() => setScheduleType('immediate')}
                        className='me-8'
                    />
                    <span className='uppercase font-bold'>Now</span>
                </label>

                <Activity
                    mode={workflowType === WORKFLOW_ACTIONS.Unpublish ? 'hidden' : 'visible'}
                >
                    <label className='flex items-center gap-2'>
                        <input
                            type="radio"
                            value="scheduled"
                            checked={scheduleType === 'scheduled'}
                            onChange={() => setScheduleType('scheduled')}
                            className='me-8'
                        />
                        <span>Schedule</span>
                    </label>
                </Activity>
            </div>

            <Activity
                mode={scheduleType === 'scheduled' ? 'visible' : 'hidden'}
            >
                <DatePicker
                    // inline
                    fixedHeight
                    placeholderText='Select Date and Time'

                    selected={publish_date_value !== 'immediate' ? new Date(publish_date_value) : null}
                    onChange={handlePublishDateChange}

                    // showTimeInput
                    showTimeSelect
                    // minTime={dayjs().add(5, 'minutes').toDate()}
                    // maxTime={dayjs().hour(23).minute(59).toDate()}
                    minDate={dayjs().toDate()}
                    maxDate={dayjs().add(30, 'days').toDate()}

                    timeIntervals={5}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    timeFormat="HH:mm"

                    className='w-full! p-2!'
                />
            </Activity>

            {/* <Activity
                mode={publish_date_value !== 'immediate' ? 'visible' : 'hidden'}
            >
                <div className='flex flex-col items-center gap-y-2 my-2'>
                    <Button
                        as='div'
                        inputVariant='common'
                        inputSize='full'
                    >
                        Timezone UTC+00:00 Western European Time
                    </Button>

                    <p
                        className='text-dark-orange'
                    >
                        Timezone data is purely informative, needs to be checked before publishing.
                    </p>
                </div>
            </Activity> */}

            <Activity
                mode={workflowType === WORKFLOW_ACTIONS.Unpublish ? 'hidden' : 'visible'}
            >
                <hr className='my-4' />

                <p className='mb-2 font-bold'>Display Date details</p>

                <div className='flex items-center gap-4 mt-2'>
                    <p className='font-semibold'>Match display time to publish time</p>

                    <SystemSwitch
                        label=''
                        checked={matchDisplayTimeToPublishTime}
                        handleToggle={(checked) => handleToggleMatchDisplayTimeToPublishTime(checked)}
                    />
                </div>

                <p className='mb-2'>Display date</p>

                <DatePicker
                    fixedHeight
                    placeholderText='Select Date and Time'

                    // selected={display_date_value !== 'immediate' ? new Date(display_date_value) : null}
                    selected={displayed_display_date_value}
                    onChange={handleDisplayDateChange}

                    showTimeSelect
                    // minTime={dayjs().add(5, 'minutes').toDate()}
                    // maxTime={dayjs().hour(23).minute(59).toDate()}
                    // minDate={dayjs().toDate()}
                    // maxDate={dayjs().add(5, 'days').toDate()}

                    timeIntervals={15}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    timeFormat="HH:mm"

                    className={clsx('w-full! p-2!', matchDisplayTimeToPublishTime ? 'opacity-50 cursor-not-allowed' : '')}

                    disabled={matchDisplayTimeToPublishTime}
                />
            </Activity>
        </React.Fragment>
    )
}

const StoryWorkflowMenu: React.FC<ICommonStoryMenuProps> = (props) => {
    const { type, buttonProps, buttonState = { type: 'enabled', tooltip: '' } } = props

    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { showSnackbar } = useSnackbar()
    const [, setSearchParams] = useSearchParams()

    const { story, state } = useAppSelector((state) => state.editor)

    const methods = useForm<StoryWorkflowFormData>({
        resolver: yupResolver(workflowSchema),
        mode: 'all',
        defaultValues: {
            type,
            publish_date_value: 'immediate',
            display_date_value: 'immediate',
        }
    })

    const { trigger } = useFormContext<StoryFormData>()

    const handleUpdateStoryWorkflow = () => {
        const data = methods.getValues()

        if (story) {
            dispatch(updateStoryWorkflowAsyncAction({ id: story._id, data }))
                .then((payload) => {
                    const newStory = (payload.payload) as ISystemStory

                    if (payload.meta.requestStatus === 'fulfilled') {
                        setSearchParams((prev) => {
                            if (prev.has(generateSearchParam("versions", "id"))) {
                                prev.delete(generateSearchParam("versions", "id"))
                            }

                            return prev;
                        })

                        let message = ''

                        if (
                            type === WORKFLOW_ACTIONS.Publish
                            && newStory.scheduleObject
                        ) {
                            // showSnackbar(`This story has a scheduled action (${story.scheduleObject.workflow}) on ${renderDateTime(story.scheduleObject.action_at)}`, 'warning');
                            message = `Story scheduled to be published on ${renderDateTime(newStory.scheduleObject.action_at)}`
                        }
                        else {
                            message = `Story ${type.toLowerCase()} successfully!`
                        }

                        showSnackbar(message, 'success')
                    }
                    else {
                        const message = (payload as any)?.error?.message || 'An error occurred'
                        showSnackbar(`Error - ${type.toLowerCase()} story: ${message}`, 'error')

                        setTimeout(() => {
                            // refresh page to reset to state
                            navigate(0)
                        }, 1000);
                    }
                })
        }
    }

    const handleMouseOver = async () => {
        await trigger()
    }

    return (
        <ControlledSystemDialog
            dialogProps={{
                title: `${type} Story`,
                maxWidth: 'sm'
            }}
            trigger={{
                component: (
                    <Button
                        inputVariant='white_text_blue_bg'
                        isLoading={state === 'workflow-updating'}
                        inputSize='xs'
                        tooltip={buttonState.tooltip}
                        disabled={buttonState.type !== 'enabled'}
                        onMouseOver={handleMouseOver}
                        {...buttonProps}
                    >
                        {type}
                    </Button>
                ),
                disabled: buttonState.type !== 'enabled',
            }}
            content={{
                component: (
                    <FormProvider {...methods} >
                        <StoryWorkflowModalContent type={type} />
                    </FormProvider>
                )
            }}
            actionsProps={{
                submit: {
                    label: type,
                    onClick: handleUpdateStoryWorkflow,
                    disabled: !methods.formState.isValid,
                    tooltip: !methods.formState.isValid ? generateFormErrorsTooltip(
                        methods.formState.errors,
                        'Please fix form values in order to schedule the story',
                    ) : '',
                },
                cancel: {
                    label: 'Cancel'
                }
            }}
        />
    )
}

export const UnscheduleStoryMenu = () => {
    const dispatch = useAppDispatch()
    const { story, state } = useAppSelector((state) => state.editor)

    const handleCancelWorkflow = () => {
        dispatch(cancelStoryWorkflowAsyncAction({ id: story!._id }))
    }

    return (
        <ControlledSystemDialog
            dialogProps={{
                title: "Unschedule Story",
                maxWidth: 'sm'
            }}
            trigger={{
                component: (
                    <Button
                        type="button"
                        inputVariant="white_text_red_bg"
                        showBorders={false}
                    >
                        Unschedule Story
                    </Button>
                )
            }}
            content={{
                component: (
                    <div
                        className='space-y-4'
                    >
                        <p>Are you sure you want to unschedule this story? Your story will no longer be published on the date and time below.</p>
                        <p className='font-bold'>{story?.scheduleObject ? renderDateTime(story?.scheduleObject?.action_at) : 'N/A'}</p>
                    </div>
                )
            }}
            actionsProps={{
                submit: {
                    label: 'Unschedule Story',
                    onClick: handleCancelWorkflow,
                    isLoading: state === 'workflow-updating',
                    disabled: state === 'workflow-updating',
                },
                cancel: {
                    label: 'Cancel'
                }
            }}
        />
    )
}

export const StoryWorkflowButton = () => {
    const { story, mode, lastSavedVersionId, lastPublishedVersionId, hasContentElementsChanged } = useAppSelector((state) => state.editor)

    const { formState, getValues } = useFormContext<StoryFormData>()

    const publishOrUpdateButtonState = useMemo(() => {
        // console.debug('StoryWorkflow.publishOrUpdateButtonState', { formState })

        if (mode !== 'editing') {
            return {
                type: 'disabled',
                tooltip: 'Story is in readonly mode'
            }
        }

        if (!formState.isValid) {

            const message = generateFormErrorsTooltip(
                formState.errors,
                'There are mandatory fields missing in order to publish the story',
            )

            return {
                type: 'disabled',
                tooltip: message
            }
        }

        if (!story?.saved_story_version) {
            return {
                type: 'disabled',
                tooltip: `Save before ${story?.story_workflow === WORKFLOWS.Published ? 'update' : 'publish'}`
            }
        }

        if (
            Object.entries(formState.dirtyFields).length > 0
            ||
            hasContentElementsChanged
        ) {
            return {
                type: 'disabled',
                tooltip: 'Save before publish'
            }
        }

        if (lastSavedVersionId === lastPublishedVersionId) {
            if (formState.isDirty) {
                return {
                    type: 'disabled',
                    tooltip: 'Save before publish'
                }
            }

            return {
                type: 'disabled',
                tooltip: 'Story is already published'
            }
        }

        const content_elements_value = getValues('content_elements')

        if (content_elements_value.length === 0) {
            return {
                type: 'disabled',
                tooltip: 'Add content before publishing'
            }
        }

        return {
            type: 'enabled',
            tooltip: 'Publish Story'
        }
    }, [formState, story, mode, hasContentElementsChanged])

    const unpublishButtonState = useMemo(() => {
        if (mode !== 'editing') {
            return {
                type: 'disabled',
                tooltip: 'Story is in readonly mode'
            }
        }

        if (story?.story_workflow !== WORKFLOWS.Published) {
            return {
                type: 'disabled',
                tooltip: 'Only published stories can be unpublished'
            }
        }

        return {
            type: 'enabled',
            tooltip: 'Unpublish Story'
        }
    }, [formState, story, mode])

    if (story?.story_workflow === WORKFLOWS.Published) {
        return (
            <ButtonGroup
                triggerOptions={{
                    variant: 'white_text_blue_bg'
                }}
                menuOptions={{
                    position: 'right'
                }}
                labelComponent={(
                    <StoryWorkflowMenu
                        type={WORKFLOW_ACTIONS.Update}
                        buttonState={publishOrUpdateButtonState}
                        buttonProps={{
                            showHover: false,
                            inputSize: 'xs',
                            className: 'rounded-e-none! border-none!'
                        }}
                    />
                )}
                buttons={[
                    {
                        component: (
                            <StoryWorkflowMenu
                                type={WORKFLOW_ACTIONS.Unpublish}
                                buttonState={unpublishButtonState}
                                buttonProps={{
                                    inputVariant: 'red_text_white_bg',
                                    showHover: false,
                                    inputSize: 'xs',
                                }}
                            />
                        ),
                        shouldCloseOnClick: true
                    },

                    ...(story.scheduleObject ? [
                        {
                            component: (
                                <UnscheduleStoryMenu />
                            ),
                            shouldCloseOnClick: true
                        }
                    ] : [])
                ]}
            />
        )
    }

    if (story?.scheduleObject) {
        return (
            <ButtonGroup
                triggerOptions={{
                    variant: 'white_text_blue_bg'
                }}
                menuOptions={{
                    position: 'right'
                }}
                labelComponent={(
                    <StoryWorkflowMenu
                        type={WORKFLOW_ACTIONS.Publish}
                        buttonState={publishOrUpdateButtonState}
                        buttonProps={{
                            showHover: false,
                            inputSize: 'xs',
                            className: 'rounded-e-none! border-none!'
                        }}
                    />
                )}
                buttons={[
                    {
                        component: (
                            <UnscheduleStoryMenu />
                        ),
                        shouldCloseOnClick: true
                    }
                ]}
            />
        )
    }

    return (
        <StoryWorkflowMenu type={WORKFLOW_ACTIONS.Publish} buttonState={publishOrUpdateButtonState} />
    )
}